import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import {
  expireGroupOrder,
  runGroupOrderExpirySweep,
  runGroupOrderHostReminderSweep,
  shortenGroupOrderDeadline,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  GroupOrderSnapshot,
  ExpireGroupOrderBody,
  GroupOrderSweepBody,
  ExpirySweepSummary,
  HostReminderSweepSummary,
  ShortenDeadlineBody,
} from '@/types/group-order';

/**
 * All are one-shot actions rather than cached reads: there is no group-order list in the
 * dashboard to invalidate, and the result of each run is the thing worth showing.
 */
export function useExpireGroupOrder(): UseMutationResult<
  GroupOrderSnapshot,
  Error,
  { groupOrderId: string } & ExpireGroupOrderBody
> {
  const { getIdToken } = useAuth();

  return useMutation({
    mutationFn: async ({ groupOrderId, ...body }) => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return expireGroupOrder(groupOrderId, body, token);
    },
  });
}

export function useRunExpirySweep(): UseMutationResult<ExpirySweepSummary, Error, GroupOrderSweepBody> {
  const { getIdToken } = useAuth();

  return useMutation({
    mutationFn: async body => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return runGroupOrderExpirySweep(body, token);
    },
  });
}

export function useShortenGroupOrderDeadline(): UseMutationResult<
  GroupOrderSnapshot,
  Error,
  { groupOrderId: string } & ShortenDeadlineBody
> {
  const { getIdToken } = useAuth();

  return useMutation({
    mutationFn: async ({ groupOrderId, ...body }) => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return shortenGroupOrderDeadline(groupOrderId, body, token);
    },
  });
}

export function useRunHostReminderSweep(): UseMutationResult<
  HostReminderSweepSummary,
  Error,
  GroupOrderSweepBody
> {
  const { getIdToken } = useAuth();

  return useMutation({
    mutationFn: async body => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return runGroupOrderHostReminderSweep(body, token);
    },
  });
}
